import React, { useState, useEffect } from 'react';
import { Users, Mail, MessageSquare, Calendar, BarChart3, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminPage = () => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptions, setSubscriptions] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventRSVPs, setEventRSVPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    pendingPrayerRequests: 0,
    pendingContactSubmissions: 0,
    totalEvents: 0,
    totalRSVPs: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'events', name: 'Events & RSVPs', icon: Calendar },
    { id: 'subscriptions', name: 'Subscriptions', icon: Users },
    { id: 'prayers', name: 'Prayer Requests', icon: MessageSquare },
    { id: 'contacts', name: 'Contact Forms', icon: Mail }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [subsResponse, prayersResponse, contactsResponse, eventsResponse] = await Promise.all([
        fetch(`${API}/subscriptions`),
        fetch(`${API}/prayer-requests`),
        fetch(`${API}/contact-submissions`),
        fetch(`${API}/events?status=all`)
      ]);

      const subscriptionsData = await subsResponse.json();
      const prayersData = await prayersResponse.json();
      const contactsData = await contactsResponse.json();
      const eventsData = await eventsResponse.json();

      setSubscriptions(subscriptionsData);
      setPrayerRequests(prayersData);
      setContactSubmissions(contactsData);
      setEvents(eventsData);

      // Calculate total RSVPs for the December 11th event
      if (eventsData.length > 0) {
        try {
          const rsvpResponse = await fetch(`${API}/events/${eventsData[0].id}/rsvps`);
          if (rsvpResponse.ok) {
            const rsvps = await rsvpResponse.json();
            setEventRSVPs(rsvps);
            setStats({
              totalSubscriptions: subscriptionsData.length,
              pendingPrayerRequests: prayersData.filter(p => p.status === 'pending').length,
              pendingContactSubmissions: contactsData.filter(c => c.status === 'pending').length,
              totalEvents: eventsData.length,
              totalRSVPs: rsvps.length
            });
          }
        } catch (error) {
          console.warn('Could not fetch RSVPs');
        }
      } else {
        setStats({
          totalSubscriptions: subscriptionsData.length,
          pendingPrayerRequests: prayersData.filter(p => p.status === 'pending').length,
          pendingContactSubmissions: contactsData.filter(c => c.status === 'pending').length,
          totalEvents: eventsData.length,
          totalRSVPs: 0
        });
      }

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportRSVPs = () => {
    const csvContent = eventRSVPs.map(rsvp => 
      `${rsvp.name},${rsvp.email},${rsvp.phone || ''},${rsvp.guests_count},${rsvp.dietary_restrictions || ''},${rsvp.needs_transportation ? 'Yes' : 'No'},${rsvp.pickup_location || ''},${rsvp.can_provide_rides ? 'Yes' : 'No'},${rsvp.vehicle_capacity || ''}`
    ).join('\n');
    
    const csvHeader = 'Name,Email,Phone,Guests,Dietary Restrictions,Needs Transportation,Pickup Location,Can Provide Rides,Vehicle Capacity\n';
    const blob = new Blob([csvHeader + csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'December_11_Event_RSVPs.csv';
    a.click();
  };

  if (loading) {
    return (
      <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-[#f17b21] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 font-sans">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 lg:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2 font-display">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 font-sans">
              Manage your ministry content and community engagement
            </p>
          </div>
          
          {/* User Info & Logout */}
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="font-semibold text-gray-800 font-sans">{user?.primaryEmail || 'Admin'}</p>
              <p className="text-sm text-gray-600 font-sans">Administrator</p>
            </div>
            <button
              onClick={signOut}
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Total Subscriptions</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.totalSubscriptions}</p>
              </div>
              <Users className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Pending Prayers</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.pendingPrayerRequests}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Pending Messages</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.pendingContactSubmissions}</p>
              </div>
              <Mail className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Active Events</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.totalEvents}</p>
              </div>
              <Calendar className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Event RSVPs</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.totalRSVPs}</p>
              </div>
              <User className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="border-b border-gray-100">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-colors duration-300 font-sans whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-[#f17b21] border-b-2 border-[#f17b21]'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6 lg:p-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Ministry Dashboard Overview</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-800 mb-4 font-sans">Recent Activity</h4>
                    <div className="space-y-3">
                      {prayerRequests.slice(0, 3).map((prayer) => (
                        <div key={prayer.id} className="flex items-center space-x-3">
                          <MessageSquare className="w-4 h-4 text-[#f17b21]" />
                          <div className="flex-1">
                            <p className="text-sm font-sans">Prayer request from {prayer.name || 'Anonymous'}</p>
                            <p className="text-xs text-gray-500 font-sans">{formatDate(prayer.submitted_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-800 mb-4 font-sans">December 11th Event</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="font-sans">Total RSVPs:</span>
                        <span className="font-bold font-sans">{stats.totalRSVPs}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-sans">Need Transportation:</span>
                        <span className="font-bold font-sans">
                          {eventRSVPs.filter(r => r.needs_transportation).length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-sans">Can Provide Rides:</span>
                        <span className="font-bold font-sans">
                          {eventRSVPs.filter(r => r.can_provide_rides).length}
                        </span>
                      </div>
                      <button
                        onClick={() => setActiveTab('events')}
                        className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans mt-4"
                      >
                        View Event Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Events Tab */}
            {activeTab === 'events' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">December 11th Event Management</h3>
                
                {events.length > 0 && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] text-white p-6 rounded-xl">
                      <h4 className="text-xl font-bold font-display mb-2">{events[0].title}</h4>
                      <p className="opacity-90 font-sans">December 11, 2025 • 8:00 PM - 10:00 PM</p>
                      <p className="opacity-90 font-sans">Rogers Theater, Dallas Baptist University</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-white p-6 rounded-xl border">
                        <h5 className="font-bold text-gray-800 mb-4 font-sans">Event Summary</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="font-sans">Total RSVPs:</span>
                            <span className="font-bold font-sans">{eventRSVPs.length}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-sans">Total Guests:</span>
                            <span className="font-bold font-sans">
                              {eventRSVPs.reduce((total, rsvp) => {
                                const count = rsvp.guests_count === '6+' ? 6 : parseInt(rsvp.guests_count) || 1;
                                return total + count;
                              }, 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-sans">Need Rides:</span>
                            <span className="font-bold font-sans text-blue-600">
                              {eventRSVPs.filter(r => r.needs_transportation).length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-sans">Can Provide Rides:</span>
                            <span className="font-bold font-sans text-green-600">
                              {eventRSVPs.filter(r => r.can_provide_rides).length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-sans">Dietary Restrictions:</span>
                            <span className="font-bold font-sans">
                              {eventRSVPs.filter(r => r.dietary_restrictions && r.dietary_restrictions.trim()).length}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white p-6 rounded-xl border">
                        <h5 className="font-bold text-gray-800 mb-4 font-sans">Actions</h5>
                        <div className="space-y-3">
                          <button 
                            onClick={exportRSVPs}
                            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans"
                          >
                            Export CSV
                          </button>
                          <button 
                            onClick={() => {
                              const emails = eventRSVPs.map(r => r.email).join(',');
                              window.open(`mailto:?bcc=${emails}&subject=December 11th Event Update`, '_blank');
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans"
                          >
                            Email All Attendees
                          </button>
                          <button 
                            onClick={() => window.open('/DBU', '_blank')}
                            className="w-full border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans"
                          >
                            View Event Page
                          </button>
                        </div>
                      </div>

                      <div className="bg-white p-6 rounded-xl border">
                        <h5 className="font-bold text-gray-800 mb-4 font-sans">Transportation</h5>
                        <div className="space-y-2 text-sm">
                          <p className="font-sans">
                            <strong>Ride Requests:</strong> {eventRSVPs.filter(r => r.needs_transportation).length}
                          </p>
                          <p className="font-sans">
                            <strong>Ride Providers:</strong> {eventRSVPs.filter(r => r.can_provide_rides).length}
                          </p>
                          <p className="font-sans">
                            <strong>Available Seats:</strong> {
                              eventRSVPs
                                .filter(r => r.can_provide_rides && r.vehicle_capacity)
                                .reduce((total, r) => total + (r.vehicle_capacity === '6+' ? 6 : parseInt(r.vehicle_capacity) || 0), 0)
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* RSVPs Table - Expanded Default View */}
                    <div className="bg-white rounded-xl p-6 border">
                      <div className="flex items-center justify-between mb-4">
                        <h5 className="text-lg font-bold text-gray-800 font-display">December 11th Event RSVPs ({eventRSVPs.length} Registered)</h5>
                        <div className="flex space-x-2">
                          <button 
                            onClick={exportRSVPs}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans text-sm"
                          >
                            📊 Export CSV
                          </button>
                          <button 
                            onClick={() => {
                              const emails = eventRSVPs.map(r => r.email).join(',');
                              window.open(`mailto:?bcc=${emails}&subject=December 11th Event - Important Update`, '_blank');
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans text-sm"
                          >
                            ✉️ Email All
                          </button>
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Attendee Info</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Contact Details</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Guest Count</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Dietary Needs</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Transportation</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Special Requests</th>
                              <th className="text-left py-3 px-3 font-semibold text-gray-800 font-sans">Status & Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eventRSVPs.map((rsvp) => (
                              <tr key={rsvp.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-4 px-3 font-sans">
                                  <div>
                                    <p className="font-bold text-gray-800">{rsvp.name}</p>
                                    <p className="text-xs text-gray-500">ID: {rsvp.id.slice(-12)}</p>
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div>
                                    <p className="text-sm font-semibold text-gray-800">{rsvp.email}</p>
                                    <p className="text-xs text-gray-600">
                                      📞 {rsvp.phone || 'No phone provided'}
                                    </p>
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div className="text-center">
                                    <p className="text-lg font-bold text-[#f17b21]">{rsvp.guests_count}</p>
                                    <p className="text-xs text-gray-500">
                                      {rsvp.transportation_guests_count && rsvp.transportation_guests_count !== rsvp.guests_count 
                                        ? `(${rsvp.transportation_guests_count} for transport)` 
                                        : 'Same for transport'}
                                    </p>
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div>
                                    {rsvp.dietary_restrictions ? (
                                      <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                                        <p className="text-xs font-semibold text-yellow-800">⚠️ Special Dietary Needs</p>
                                        <p className="text-xs text-yellow-700">{rsvp.dietary_restrictions}</p>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">No restrictions</p>
                                    )}
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div className="space-y-2">
                                    {rsvp.needs_transportation ? (
                                      <div className="bg-blue-50 border border-blue-200 rounded p-2">
                                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold block mb-1">
                                          🚗 Needs Ride
                                        </span>
                                        <p className="text-xs text-blue-700">
                                          <strong>Pickup:</strong> {rsvp.pickup_location || 'Location not specified'}
                                        </p>
                                        {rsvp.transportation_notes && (
                                          <p className="text-xs text-blue-600 italic mt-1">
                                            "{rsvp.transportation_notes}"
                                          </p>
                                        )}
                                      </div>
                                    ) : rsvp.can_provide_rides ? (
                                      <div className="bg-green-50 border border-green-200 rounded p-2">
                                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold block mb-1">
                                          🚙 Can Drive
                                        </span>
                                        <p className="text-xs text-green-700">
                                          <strong>Available:</strong> {rsvp.vehicle_capacity || 'Capacity not specified'} extra seats
                                        </p>
                                        {rsvp.transportation_notes && (
                                          <p className="text-xs text-green-600 italic mt-1">
                                            "{rsvp.transportation_notes}"
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-500 text-xs bg-gray-100 px-2 py-1 rounded">🚶 Own transport</span>
                                    )}
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div>
                                    {rsvp.special_requests ? (
                                      <div className="bg-purple-50 border border-purple-200 rounded p-2">
                                        <p className="text-xs font-semibold text-purple-800">💬 Special Requests</p>
                                        <p className="text-xs text-purple-700">{rsvp.special_requests}</p>
                                      </div>
                                    ) : (
                                      <p className="text-xs text-gray-500">No special requests</p>
                                    )}
                                  </div>
                                </td>
                                
                                <td className="py-4 px-3 font-sans">
                                  <div className="text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans mb-2 block ${
                                      rsvp.rsvp_status === 'attending' ? 'bg-green-100 text-green-800' : 
                                      rsvp.rsvp_status === 'maybe' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      {rsvp.rsvp_status === 'attending' ? '✅ Attending' :
                                       rsvp.rsvp_status === 'maybe' ? '❓ Maybe' : '❌ Cannot Attend'}
                                    </span>
                                    <p className="text-xs text-gray-500">{formatDate(rsvp.rsvp_date)}</p>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {eventRSVPs.length === 0 && (
                        <div className="text-center py-12">
                          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-xl font-bold text-gray-800 mb-2 font-display">No RSVPs Yet</h4>
                          <p className="text-gray-600 font-sans">RSVPs will appear here as people register for the event.</p>
                        </div>
                      )}
                      
                      {/* Enhanced Summary Section */}
                      {eventRSVPs.length > 0 && (
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 pt-6 border-t border-gray-200">
                          <div className="bg-blue-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-blue-600 font-display">
                              {eventRSVPs.filter(r => r.needs_transportation).length}
                            </p>
                            <p className="text-sm text-blue-700 font-sans">Need Rides</p>
                            {eventRSVPs.filter(r => r.needs_transportation).length > 0 && (
                              <p className="text-xs text-blue-600 mt-1 font-sans">
                                {eventRSVPs.filter(r => r.needs_transportation && r.pickup_location).length} with pickup locations
                              </p>
                            )}
                          </div>
                          
                          <div className="bg-green-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-green-600 font-display">
                              {eventRSVPs.filter(r => r.can_provide_rides).length}
                            </p>
                            <p className="text-sm text-green-700 font-sans">Can Drive</p>
                            <p className="text-xs text-green-600 mt-1 font-sans">
                              {eventRSVPs
                                .filter(r => r.can_provide_rides && r.vehicle_capacity)
                                .reduce((total, r) => total + (r.vehicle_capacity === '6+' ? 6 : parseInt(r.vehicle_capacity) || 0), 0)} total seats
                            </p>
                          </div>
                          
                          <div className="bg-yellow-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-yellow-600 font-display">
                              {eventRSVPs.filter(r => r.dietary_restrictions && r.dietary_restrictions.trim()).length}
                            </p>
                            <p className="text-sm text-yellow-700 font-sans">Dietary Needs</p>
                            <p className="text-xs text-yellow-600 mt-1 font-sans">
                              Special catering required
                            </p>
                          </div>
                          
                          <div className="bg-purple-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-purple-600 font-display">
                              {eventRSVPs.reduce((total, rsvp) => {
                                const count = rsvp.guests_count === '6+' ? 6 : parseInt(rsvp.guests_count) || 1;
                                return total + count;
                              }, 0)}
                            </p>
                            <p className="text-sm text-purple-700 font-sans">Total Expected</p>
                            <p className="text-xs text-purple-600 mt-1 font-sans">
                              Including all guests
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Email Subscriptions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Type</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Subscribed</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-sans">{sub.email}</td>
                          <td className="py-3 px-4 font-sans">{sub.name || 'Not provided'}</td>
                          <td className="py-3 px-4 font-sans">{sub.subscription_type}</td>
                          <td className="py-3 px-4 font-sans">{formatDate(sub.subscribed_at)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                              sub.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Prayer Requests Tab */}
            {activeTab === 'prayers' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Prayer Requests</h3>
                <div className="space-y-6">
                  {prayerRequests.map((prayer) => (
                    <div key={prayer.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-gray-800 font-sans">
                            {prayer.name || 'Anonymous Request'}
                          </h4>
                          <p className="text-sm text-gray-500 font-sans">
                            {formatDate(prayer.submitted_at)}
                            {prayer.email && ` • ${prayer.email}`}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {prayer.is_urgent && (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold font-sans">
                              Urgent
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                            prayer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            prayer.status === 'prayed_for' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {prayer.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4 font-sans leading-relaxed">
                        {prayer.request_text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Submissions Tab */}
            {activeTab === 'contacts' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Contact Form Submissions</h3>
                <div className="space-y-6">
                  {contactSubmissions.map((contact) => (
                    <div key={contact.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-gray-800 font-sans">{contact.name}</h4>
                          <p className="text-sm text-gray-500 font-sans">
                            {contact.email} • {formatDate(contact.submitted_at)}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                          contact.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {contact.status.toUpperCase()}
                        </span>
                      </div>
                      <h5 className="font-semibold text-gray-800 mb-2 font-sans">Subject: {contact.subject}</h5>
                      <p className="text-gray-700 mb-4 font-sans leading-relaxed">
                        {contact.message}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;